import React, { useRef, useState, useEffect, useCallback } from "react";

const items = [
  "Вальс до-диез минор, Op. 64 No. 2",
  "Ноктюрн до-диез минор, op. posth.",
  "Этюд до минор, Op. 10 No. 12 «Революционный»",
  "Фантазия-экспромт до-диез минор, Op. 66",
  "Этюд до минор, Op. 25 No. 12",
  "Ноктюрн ми-бемоль мажор, Op. 9 No. 2",
  "Баллада №1 соль минор, Op. 23",
  "Соната No. 2 си-бемоль минор, Op. 35 — «Похоронный марш»",
  "Вальс ми минор, op. posth.",
];

export default function Marquee() {
  // 4× duplication for seamless loop and so the half-width is meaningful
  const repeated = [...items, ...items, ...items, ...items];

  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const halfRef = useRef(0); // width of one full set
  const lastTsRef = useRef(0);
  const rafRef = useRef(0);
  const draggingRef = useRef(false);
  const dragStart = useRef({ x: 0, offset: 0 });
  const speedRef = useRef(90);
  const [isDragging, setIsDragging] = useState(false);

  const normalize = (x) => {
    const half = halfRef.current;
    if (half <= 0) return x;
    while (x <= -half) x += half;
    while (x > 0) x -= half;
    return x;
  };

  const tick = useCallback((ts) => {
    const el = trackRef.current;
    if (!el) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    if (!lastTsRef.current) lastTsRef.current = ts;
    const dt = Math.min((ts - lastTsRef.current) / 1000, 0.05);
    lastTsRef.current = ts;
    if (!draggingRef.current && halfRef.current > 0) {
      offsetRef.current = normalize(offsetRef.current - speedRef.current * dt);
      el.style.transform = `translate3d(${offsetRef.current.toFixed(2)}px,0,0)`;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const measure = () => {
      // total scroll width / number of duplicates (4)
      halfRef.current = el.scrollWidth / 4;
    };

    // Mobile slower, desktop faster
    const updateSpeed = () => {
      speedRef.current = window.innerWidth < 768 ? 50 : 90;
    };

    measure();
    updateSpeed();

    // Re-measure when layout / fonts settle
    const onLoad = () => measure();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    window.addEventListener("load", onLoad);
    window.addEventListener("resize", updateSpeed);
    window.addEventListener("resize", measure);

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("load", onLoad);
      window.removeEventListener("resize", updateSpeed);
      window.removeEventListener("resize", measure);
    };
  }, [tick]);

  const onPointerDown = (e) => {
    const x = e.touches?.[0]?.clientX ?? e.clientX ?? 0;
    draggingRef.current = true;
    dragStart.current = { x, offset: offsetRef.current };
    setIsDragging(true);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const x = e.touches?.[0]?.clientX ?? e.clientX ?? 0;
    const dx = x - dragStart.current.x;
    offsetRef.current = normalize(dragStart.current.offset + dx);
    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${offsetRef.current.toFixed(2)}px,0,0)`;
    }
  };
  const onPointerUp = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setIsDragging(false);
    lastTsRef.current = 0;
  };

  useEffect(() => {
    const up = () => onPointerUp();
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    window.addEventListener("touchcancel", up);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
      window.removeEventListener("touchcancel", up);
    };
  }, []);

  return (
    <div
      className={`relative py-8 md:py-10 border-y border-[#F2E9DE]/10 overflow-hidden select-none ${
        isDragging ? "cursor-grabbing" : "cursor-grab"
      }`}
      data-testid="marquee-section"
      onMouseDown={onPointerDown}
      onMouseMove={onPointerMove}
      onTouchStart={onPointerDown}
      onTouchMove={onPointerMove}
    >
      <div
        className="absolute inset-y-0 left-0 w-16 md:w-24 z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, #080605 0%, rgba(8,6,5,0) 100%)",
        }}
      />
      <div
        className="absolute inset-y-0 right-0 w-16 md:w-24 z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(270deg, #080605 0%, rgba(8,6,5,0) 100%)",
        }}
      />
      <div
        ref={trackRef}
        className="flex whitespace-nowrap"
        style={{
          transform: "translate3d(0,0,0)",
          willChange: "transform",
          backfaceVisibility: "hidden",
          WebkitFontSmoothing: "antialiased",
        }}
      >
        {repeated.map((it, i) => (
          <span
            key={i}
            className="inline-flex items-center font-serif italic font-light text-[#F2E9DE]/85 mx-6 md:mx-10 pointer-events-none"
            style={{ fontSize: "clamp(1.2rem, 2.4vw, 2.1rem)" }}
          >
            {it}
            <span className="mx-6 md:mx-10 text-[#E57324]">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}
